import { useTheme } from '@dhl-ui/theme'
import { bottomMenuHeightSave } from '../bottom-menu'
import { type PageContentProps } from './types'

type Props = Pick<
  PageContentProps,
  | 'isMaximizedCentered'
  | 'isFullView'
  | 'hasBottomMenu'
  | 'hasMaxSize'
  | 'sx'
  | 'hasTopIndentation'
  | 'hasBottomIndentation'
>

type Style = {
  px?: string
  pt?: string
  pb?: string
  p?: string
  display?: string
  flexDirection?: string
  width?: string
  height?: string
  maxWidth?: string
  margin?: string
  boxSizing?: 'border-box'
  '@supports (height: 100dvh)'?: {
    height: string
  }
  '> div:first-of-type'?: {
    flex: number
    height: string
  }
} & PageContentProps['sx']

const getMaxSize = (arg: Props['hasMaxSize']) => {
  switch (arg) {
    case 'XS':
      return 50
    case 'SM':
      return 70
    case 'LG':
      return 180
    case 'MD':
    default:
      return 120
  }
}

export const useGetStyling = ({
  isMaximizedCentered,
  isFullView,
  hasMaxSize,
  hasBottomMenu,
  hasTopIndentation,
  hasBottomIndentation,
  sx,
}: Props): Style | undefined => {
  const { breakpoints, variant, spacing } = useTheme()

  let baseStyle: Style | undefined

  switch (variant) {
    /**
     * For our mobile variant we have decided they do not have any indentation, in order to create
     * full size rows. When your single row inside it does need indentation you can use our
     * `Page.ContentRow` or `PageWithRefresh.ContentRow`. But most new designs will use the full width :)
     */
    case 'mobile':
    case 'tablet':
      baseStyle = sx
      break
    case 'desktop':
      baseStyle = {
        ...sx,
        px: spacing(3),
        pt: spacing(3),
        pb: spacing(2),
        width: '100%',
        boxSizing: 'border-box',
        ...(isMaximizedCentered ? { margin: '0 auto' } : {}),
        ...(hasMaxSize ? { maxWidth: spacing(getMaxSize(hasMaxSize)) } : {}),
        [breakpoints.up('lg')]: {
          px: spacing(4),
          pt: spacing(3.5),
        },
        [breakpoints.up('xl')]: {
          px: spacing(5),
          pt: spacing(4),
        },
      }
      break
    case 'lg':
    case 'md':
      baseStyle = {
        p: spacing(hasTopIndentation ? 8 : 0, 0, hasBottomIndentation ? 8 : 0),

        ...(isMaximizedCentered ? { margin: '0 auto' } : {}),
        ...(hasMaxSize ? { maxWidth: spacing(getMaxSize(hasMaxSize)) } : {}),
      }
      break
    case 'xl':
    default:
      baseStyle = { p: spacing(1) }
      break
  }

  if (isFullView) {
    // Same logic is applied to theme creation MuiToolbar, do not adjust without approval from UX
    const heightHeader = spacing(variant === 'xl' ? 7 : 8)

    const reservedHeight = `${heightHeader} ${hasBottomMenu ? `- ${bottomMenuHeightSave}` : ''}`

    baseStyle = {
      ...(baseStyle ?? {}),
      display: 'flex',
      flexDirection: 'column',

      height: `calc(100vh - ${reservedHeight})`,

      '@supports (height: 100dvh)': {
        height: `calc(100dvh - ${reservedHeight})`,
      },

      '> div:first-of-type': {
        flex: 1,
        height: '100%',
      },
    }
  }

  return baseStyle
}
